import { formatNumber, numberToCurrency } from '@/utils'
import type { SectionValidation } from './types'

// The engine's check names, in words a customer reads.
const SECTION_LABELS: Record<string, string> = {
  holdings_market_value: 'Holdings value',
  security_sales: 'Sales',
  security_purchases: 'Purchases',
  realized_gains: 'Realized gains',
  shares_moved: 'Shares moved',
  activity_rows: 'Activity rows',
  unvested_awards: 'Unvested stock awards',
}

// These checks count shares and rows, not dollars.
const COUNT_UNITS: Record<string, { one: string; many: string }> = {
  shares_moved: { one: 'share', many: 'shares' },
  activity_rows: { one: 'row', many: 'rows' },
}

const sectionLabel = (section: string) =>
  SECTION_LABELS[section] ?? section.replace(/_/g, ' ')

const formatGap = (section: string, gap: number) => {
  const unit = COUNT_UNITS[section]
  if (!unit) return numberToCurrency(gap)
  return `${formatNumber(gap)} ${gap === 1 ? unit.one : unit.many}`
}

// Say why a section failed. An unreadable printed total comes first: the engine's printed figure
// is then only the blocks it could read, so the gap would mislead.
export const describeSectionFailure = (section: SectionValidation): string => {
  const label = sectionLabel(section.section)
  if (section.anchorMissing) return `${label}: printed total unreadable`
  if (section.delta != null && section.delta !== 0) {
    return `${label}: off by ${formatGap(section.section, Math.abs(section.delta))}`
  }
  return `${label}: did not match`
}

// The subject of a one-sentence reason: "Holdings are $1,240.00 short of the printed total."
const SECTION_SUBJECTS: Record<string, string> = {
  holdings_market_value: 'Holdings',
  security_sales: 'Sales',
  security_purchases: 'Purchases',
  realized_gains: 'Realized gains',
  shares_moved: 'Shares moved',
  activity_rows: 'Activity rows',
  unvested_awards: 'Unvested stock awards',
}

const sectionSubject = (section: string) =>
  SECTION_SUBJECTS[section] ?? sectionLabel(section)

// Purchases print negative, so short and over compare sizes, not signed values.
const shortfallWord = (section: SectionValidation): string | null => {
  const { printedTotal, extractedTotal } = section
  if (printedTotal != null && extractedTotal != null) {
    if (Math.abs(extractedTotal) === Math.abs(printedTotal)) return null
    return Math.abs(extractedTotal) < Math.abs(printedTotal)
      ? 'short of'
      : 'over'
  }
  if (section.delta == null || section.delta === 0) return null
  return section.delta < 0 ? 'short of' : 'over'
}

export const sectionFailureSentence = (section: SectionValidation): string => {
  const subject = sectionSubject(section.section)
  if (section.anchorMissing) {
    return `The ${subject.toLowerCase()} total could not be read.`
  }
  const word = shortfallWord(section)
  if (word == null || section.delta == null) {
    return `${subject} do not match the printed total.`
  }
  return `${subject} are ${formatGap(section.section, Math.abs(section.delta))} ${word} the printed total.`
}
